/**
 * DeviceModalPreview Component
 * 
 * Renders a preview of the device modal with configurable styles.
 * Supports both scaled (for card thumbnails) and full-size modes.
 */

import React, { Suspense, useMemo, useState, useCallback } from 'react';
import { DeviceModalConfig, IntelliSpaceDevice } from '../deviceModals/types';
import { getTemplateForDeviceType } from '../deviceModals/templates';

export interface DeviceModalPreviewProps {
  /** Device type to preview */
  deviceType: string;
  /** Template and style configuration */
  config: DeviceModalConfig;
  /** Scale factor (0-1, defaults to 1 for full size) */
  scale?: number;
  /** Whether to show a border/container */
  showContainer?: boolean;
  /** Custom class name */
  className?: string;
  /** Whether preview is interactive (user can operate controls) */
  interactive?: boolean;
}

/**
 * Generate mock chart data for preview
 */
function getMockChartData(deviceType: string): any[] {
  const now = new Date();
  const data: any[] = [];
  
  // Generate 24 hours of mock data
  for (let i = 24; i >= 0; i--) {
    const time = new Date(now.getTime() - i * 60 * 60 * 1000);
    const baseTemp = 22 + Math.sin(i * 0.3) * 3;
    const baseHumidity = 60 + Math.cos(i * 0.25) * 8;
    const basePower = 150 + Math.random() * 100;
    const baseLux = 200 + Math.sin(i * 0.5) * 150;
    
    data.push({
      timestamp: time.toISOString(),
      time: time.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }),
      temperature: Math.round(baseTemp * 10) / 10,
      humidity: Math.round(baseHumidity),
      watt: Math.round(basePower),
      lux: Math.round(Math.max(0, baseLux)),
    });
  }
  
  return data;
}

/**
 * Generate mock device data for preview
 */
function getMockDevice(deviceType: string): IntelliSpaceDevice {
  const baseDevice: IntelliSpaceDevice = {
    deviceId: 'PREVIEW-DEVICE-001',
    baseSN: 'PREVIEW-001',
    deviceName: 'Preview Device',
    deviceType: deviceType,
    status: 'online',
    location: 'Preview Location',
    groupId: 'preview-group',
    groupName: 'Preview Group',
    traits: [],
    attributes: {},
    isOn: true,
    online: true
  };

  // Add device-specific mock data
  switch (deviceType) {
    case 'light':
      return {
        ...baseDevice,
        deviceName: '客廳燈',
        traits: ['OnOff', 'Brightness', 'ColorSetting'],
        isOn: true,
        brightness: 75,
        colorTemp: 4000,
        attributes: {
          colorTemperatureRange: { temperatureMinK: 2700, temperatureMaxK: 6500 }
        }
      };
    
    case 'hvac':
      return {
        ...baseDevice,
        deviceName: '臥室冷氣',
        traits: ['OnOff', 'TemperatureSetting', 'ThermostatMode', 'FanSpeed', 'Mode', 'CustomToggles'],
        isOn: true,
        temperature: 24.5,
        thermostatTemperatureSetpoint: 25,
        thermostatMode: 'cool',
        fanSpeed: 'auto',
        toggles: ['Swing', 'Eco', 'Sleep', 'Light'],
        toggleValues: {
          Swing: true,
          Eco: false,
          Sleep: false,
          Light: true
        },
        attributes: {
          temperatureRange: { min: 16, max: 32 },
          availableFanSpeeds: ['low', 'medium', 'high', 'auto'],
          sensorProfile: { temp: { Min: 16, Max: 32 } },
          availableToggles: ['Swing', 'Eco', 'Sleep', 'Light']
        }
      };
    
    case 'sensor':
    case 'th_sensor':
      return {
        ...baseDevice,
        deviceName: '溫濕度感應器',
        traits: [],
        temperature: 25.5,
        humidity: 65,
        attributes: {
          sensorProfile: {
            temp: { Min: -10, Max: 50 },
            rh: { Min: 0, Max: 100 }
          }
        }
      };
    
    case 'presence_sensor':
      return {
        ...baseDevice,
        deviceName: '人體感應器',
        traits: [],
        presence: true,
        occupancy: true,
        lux: 350,
        attributes: {
          sensorProfile: {
            lux: { Min: 0, Max: 10000 }
          },
          detectorStates: ['occupancy']
        }
      };
    
    case 'dehumidifier':
      return {
        ...baseDevice,
        deviceName: '除濕機',
        traits: ['OnOff', 'HumiditySetting', 'FanSpeed', 'Mode', 'Timer', 'CustomToggles'],
        isOn: true,
        humidity: 65,
        humiditySetpointPercent: 55,
        attributes: {
          humiditySetpointRange: { minPercent: 30, maxPercent: 80 },
          availableFanSpeeds: ['low', 'medium', 'high'],
          availableCustomModes: [
            { name: 'Mode', settings: ['auto', 'continuous', 'laundry'] }
          ],
          availableCustomToggles: ['AutoDirection', 'AirClean']
        }
      };
    
    case 'switch':
    case 'outlet':
      return {
        ...baseDevice,
        deviceName: deviceType === 'switch' ? '智能開關' : '智能插座',
        traits: ['OnOff'],
        isOn: true,
        watt: 45.5
      };
    
    case 'fan':
      return {
        ...baseDevice,
        deviceName: '電風扇',
        traits: ['OnOff', 'FanSpeed', 'Mode'],
        isOn: true,
        fanSpeed: 'medium',
        attributes: {
          availableFanSpeeds: ['low', 'medium', 'high']
        }
      };
    
    case 'curtain':
    case 'blind':
      return {
        ...baseDevice,
        deviceName: deviceType === 'curtain' ? '電動窗簾' : '電動百葉窗',
        traits: ['OpenClose'],
        openPercent: 50,
        isOpen: true
      };
    
    case 'power_meter':
      return {
        ...baseDevice,
        deviceName: '電力錶',
        traits: [],
        watt: 1250.5,
        wattHour: 45678,
        voltage: 220,
        current: 5.68,
        status: 'online',
        online: true,
        isOnline: true,
        attributes: {
          sensorProfile: {
            watt: { Min: 0, Max: 10000 },
            voltage: { Min: 0, Max: 250 },
            current: { Min: 0, Max: 50 }
          }
        }
      } as IntelliSpaceDevice;
    
    case 'power_meter_3phase':
      return {
        ...baseDevice,
        deviceName: '三相電力錶',
        traits: [],
        watt: 3750.8,
        wattHour: 125000,
        voltage: 380,
        current: 9.87,
        status: 'online',
        online: true,
        isOnline: true,
        attributes: {
          sensorProfile: {
            watt: { Min: 0, Max: 50000 },
            voltage: { Min: 0, Max: 450 },
            current: { Min: 0, Max: 100 }
          }
        }
      } as IntelliSpaceDevice;
    
    case 'door_sensor':
      return {
        ...baseDevice,
        deviceName: '門磁感應器',
        traits: [],
        detected: false, // door is closed
        battery: 85,
        attributes: {
          detectorEvents: ['doorOpened', 'doorClosed']
        }
      };
    
    case 'button':
      return {
        ...baseDevice,
        deviceName: '智能按鈕',
        traits: [],
        battery: 90,
        attributes: {
          detectorEvents: ['buttonPressed']
        }
      };
    
    case 'erv':
      return {
        ...baseDevice,
        deviceName: '全熱交換器',
        traits: ['OnOff', 'FanSpeed', 'ThermostatMode', 'Mode', 'SensorState', 'TemperatureSetting', 'HumiditySetting', 'CustomModes'],
        isOn: true,
        fanSpeed: '3',
        temperature: 25,
        humidity: 55,
        thermostatMode: 'fan-only',
        thermostatTemperatureAmbient: 25,
        humidityAmbientPercent: 55,
        attributes: {
          availableCustomModes: [
            { name: 'FanSpeed', settings: ['2', '3', '4', '5', '6'], ordered: true },
            { name: 'Mode', settings: ['0', '1', '2'], ordered: true },
            { name: 'Timer', settings: ['0', '1', '2', '3', '4', '5', '6', '7', '8'], ordered: true }
          ],
          availableThermostatModes: 'fan-only,heat',
          sensorStatesSupported: [
            { name: 'PM2.5', numericCapabilities: { rawValueUnit: 'MICROGRAMS_PER_CUBIC_METER' } },
            { name: 'CarbonDioxideLevel', numericCapabilities: { rawValueUnit: 'PARTS_PER_MILLION' } }
          ],
          // ERV-specific data stored in attributes for template access
          currentModeSettings: {
            value: { FanSpeed: '3', Mode: '0', Timer: '0' }
          },
          currentSensorStateData: {
            value: [
              { name: 'PM2.5', rawValue: 12 },
              { name: 'CarbonDioxideLevel', rawValue: 650 }
            ]
          }
        }
      } as IntelliSpaceDevice;
    
    default:
      return {
        ...baseDevice,
        deviceName: '通用設備',
        traits: ['OnOff'],
        isOn: true
      };
  }
}

/**
 * Loading fallback for lazy-loaded templates
 */
const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center h-full min-h-[200px]">
    <div className="text-slate-500 text-sm">Loading preview...</div>
  </div>
);

/**
 * DeviceModalPreview Component
 */
export const DeviceModalPreview: React.FC<DeviceModalPreviewProps> = ({
  deviceType,
  config,
  scale = 1,
  showContainer = true,
  className = '',
  interactive = false
}) => {
  // Get initial mock device data
  const initialDevice = useMemo(() => getMockDevice(deviceType), [deviceType]);
  
  // Interactive state - tracks device state changes from user interactions
  const [deviceState, setDeviceState] = useState<Partial<IntelliSpaceDevice>>({});
  
  // Merge initial device with interactive state
  const mockDevice = useMemo(() => ({
    ...initialDevice,
    ...deviceState
  }), [initialDevice, deviceState]);

  // Generate mock chart data for preview
  const mockChartData = useMemo(() => getMockChartData(deviceType), [deviceType]);

  // Dynamically load template component
  const TemplateComponent = useMemo(() => {
    return React.lazy(() => getTemplateForDeviceType(deviceType));
  }, [deviceType]);

  // Interactive handler - updates local state to simulate device control
  const handleTraitControl = useCallback(async (
    _device: IntelliSpaceDevice,
    trait: string,
    action: string,
    params?: Record<string, any>
  ) => {
    if (!interactive) return;
    
    // Simulate state changes based on trait/action
    switch (trait) {
      case 'OnOff':
        setDeviceState(prev => ({ ...prev, isOn: action === 'on' }));
        break;
      case 'Brightness':
        if (params?.brightness !== undefined) {
          setDeviceState(prev => ({ ...prev, brightness: params.brightness }));
        }
        break;
      case 'ColorSetting':
        if (params?.color?.temperature !== undefined) {
          setDeviceState(prev => ({ ...prev, colorTemp: params.color.temperature }));
        }
        break;
      case 'TemperatureSetting':
        if (params?.thermostatTemperatureSetpoint !== undefined) {
          setDeviceState(prev => ({ 
            ...prev, 
            thermostatTemperatureSetpoint: params.thermostatTemperatureSetpoint 
          }));
        }
        break;
      case 'FanSpeed':
        if (params?.fanSpeed !== undefined) {
          setDeviceState(prev => ({ ...prev, fanSpeed: params.fanSpeed }));
        }
        break;
      case 'ThermostatMode':
        if (params?.thermostatMode !== undefined) {
          setDeviceState(prev => ({ ...prev, thermostatMode: params.thermostatMode }));
        }
        break;
      case 'OpenClose':
        if (params?.openPercent !== undefined) {
          setDeviceState(prev => ({ ...prev, openPercent: params.openPercent }));
        }
        break;
      case 'HumiditySetting':
        if (params?.humiditySetpointPercent !== undefined) {
          setDeviceState(prev => ({ 
            ...prev, 
            humiditySetpointPercent: params.humiditySetpointPercent 
          }));
        }
        break;
      default:
        // For other traits, try to extract and apply params
        if (params) {
          setDeviceState(prev => ({ ...prev, ...params }));
        }
    }
  }, [interactive]);

  const handleClose = useCallback(() => {
    // No-op in preview mode - don't close
  }, []);

  // The key insight: CSS transform creates a new containing block for fixed positioned descendants.
  // This means fixed elements inside will be positioned relative to this container, not the viewport.
  
  const previewWidth = 384; // max-w-sm = 384px

  // For scaled previews (template cards), use fixed height for consistent card sizes
  // For full-size preview (edit screen), let content determine height
  const isFullSize = scale >= 1;
  const scaledPreviewHeight = 700; // Fixed height for scaled card previews

  // CSS to override fixed positioning in templates for preview mode
  // This converts "fixed inset-0" to "relative" so content height is calculated
  const previewOverrideStyles = `
    .preview-modal-override > div:first-child {
      position: relative !important;
      inset: auto !important;
      min-height: auto !important;
      max-height: none !important;
      height: auto !important;
      padding: 0 !important;
    }
  `;

  return (
    <div 
      className={`${showContainer ? 'bg-slate-800/30 rounded-2xl border border-slate-700/50' : ''} ${className}`}
      style={{
        width: isFullSize ? `${previewWidth}px` : `${previewWidth * scale}px`,
        // Full-size: auto height (content-driven); Scaled: fixed height for cards
        height: isFullSize ? 'auto' : `${scaledPreviewHeight * scale}px`,
        overflow: 'hidden',
        position: 'relative',
        // Transform creates a new stacking context, making fixed elements relative to this container
        transform: 'translateZ(0)',
      }}
    >
      {/* Inject CSS to override fixed positioning */}
      <style>{previewOverrideStyles}</style>
      
      {/* 
        Transform container - creates new containing block for fixed positioning.
        Fixed elements inside will be relative to this container, not the viewport.
      */}
      <div 
        className="preview-modal-override"
        style={{
          transform: isFullSize ? 'none' : `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${previewWidth}px`,
          // Full-size: auto height; Scaled: fixed height
          height: isFullSize ? 'auto' : `${scaledPreviewHeight}px`,
          position: 'relative',
          // Enable interactions in interactive mode, disable in preview-only mode
          pointerEvents: interactive ? 'auto' : 'none',
        }}
      >
        <Suspense fallback={<LoadingFallback />}>
          {/* Use key to force re-render when config changes */}
          <TemplateComponent
            key={JSON.stringify(config)}
            device={mockDevice}
            isExecutingCommand={false}
            onTraitControl={handleTraitControl}
            onClose={handleClose}
            config={config}
            // Chart data for sensors and power meters
            thSensorChartData={mockChartData}
            isLoadingThSensorChart={false}
            energyChartData={mockChartData}
            isLoadingEnergyChart={false}
            luxChartData={mockChartData}
            isLoadingLuxChart={false}
          />
        </Suspense>
      </div>
    </div>
  );
};

/**
 * DeviceModalPreviewCard Component
 * 
 * A card wrapper for the preview with title, Edit/Apply buttons.
 * Used in the Template list page.
 */
export interface DeviceModalPreviewCardProps {
  /** Template ID */
  templateId: string;
  /** Template name */
  templateName: string;
  /** Device type */
  deviceType: string;
  /** Whether this template is currently applied */
  isApplied?: boolean;
  /** Whether this template can be deleted (false if only one template or is applied) */
  canDelete?: boolean;
  /** Template and style configuration */
  config: DeviceModalConfig;
  /** Called when Edit is clicked */
  onEdit?: () => void;
  /** Called when Apply is clicked */
  onApply?: () => void;
  /** Called when Delete is clicked */
  onDelete?: () => void;
}

export const DeviceModalPreviewCard: React.FC<DeviceModalPreviewCardProps> = ({
  templateId,
  templateName,
  deviceType,
  isApplied = false,
  canDelete = true,
  config,
  onEdit,
  onApply,
  onDelete
}) => {
  return (
    <div className={`
      relative bg-slate-800/30 rounded-xl border overflow-hidden transition-all
      ${isApplied 
        ? 'border-green-500/50 ring-2 ring-green-500/20' 
        : 'border-slate-700/50 hover:border-slate-600'
      }
    `}>
      {/* Applied Badge */}
      {isApplied && (
        <div className="absolute top-2 right-2 z-10 px-2 py-0.5 bg-green-500/20 border border-green-500/30 rounded text-xs text-green-400 font-medium">
          Applied
        </div>
      )}

      {/* Template Name */}
      <div className="px-4 py-2 border-b border-slate-700/50">
        <h4 className="text-sm font-medium text-white">{templateName}</h4>
        <span className="text-xs text-slate-500">{templateId}</span>
      </div>

      {/* Preview */}
      <div className="p-3">
        <DeviceModalPreview
          deviceType={deviceType}
          config={config}
          scale={0.5}
          showContainer={false}
        />
      </div>

      {/* Actions */}
      <div className="flex border-t border-slate-700/50">
        {/* Delete Button */}
        <button
          onClick={onDelete}
          disabled={!canDelete}
          title={!canDelete ? (isApplied ? '使用中的模板無法刪除' : '最後一個模板無法刪除') : '刪除模板'}
          className={`
            px-4 py-2 text-sm transition-colors border-r border-slate-700/50
            ${canDelete 
              ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10' 
              : 'text-slate-600 cursor-not-allowed'
            }
          `}
        >
          Delete
        </button>
        {/* Edit Button */}
        <button
          onClick={onEdit}
          className="flex-1 px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors border-r border-slate-700/50"
        >
          Edit
        </button>
        {/* Apply Button */}
        <button
          onClick={onApply}
          disabled={isApplied}
          className={`
            flex-1 px-4 py-2 text-sm transition-colors
            ${isApplied 
              ? 'text-slate-500 cursor-not-allowed' 
              : 'text-blue-400 hover:text-blue-300 hover:bg-slate-700/50'
            }
          `}
        >
          {isApplied ? 'Applied' : 'Apply'}
        </button>
      </div>
    </div>
  );
};

export default DeviceModalPreview;
